import React, { useCallback, useEffect, useState } from 'react';
import {
  BrainCircuit, Sparkles, TrendingUp, Gauge, Settings2, Zap, Users,
  AlertTriangle, CheckCircle2, RefreshCw, Loader2, ShieldAlert,
} from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { SkeletonRows, EmptyState } from './OrgPanelShared';
import { MarketplaceTab, SharingPoolsTab } from './MarketplaceSharingPanel';
import WorkforceBalanceTab from './WorkforceBalancePanel';

const SUB = [
  { id: 'insights', label: 'Insights', icon: Sparkles },
  { id: 'assign', label: 'Auto-Assign', icon: Zap },
  { id: 'balance', label: 'Balance', icon: Gauge },
  { id: 'market', label: 'Marketplace', icon: Users },
  { id: 'sharing', label: 'Sharing & Pools', icon: Users },
  { id: 'forecast', label: 'Forecast', icon: TrendingUp },
  { id: 'capacity', label: 'Capacity', icon: Gauge },
  { id: 'settings', label: 'AI Policy', icon: Settings2 },
];

const SEVERITY = {
  high: 'bg-red-50 border-red-200 text-red-700',
  medium: 'bg-amber-50 border-amber-200 text-amber-700',
  info: 'bg-emerald-50 border-emerald-200 text-emerald-700',
};

const BADGE_STYLES = {
  gold: 'bg-amber-100 text-amber-800',
  silver: 'bg-slate-200 text-slate-700',
  bronze: 'bg-orange-100 text-orange-800',
};

export default function AIWorkforcePanel() {
  const [sub, setSub] = useState('insights');
  return (
    <div className="space-y-4" data-testid="ai-workforce-panel">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
          <BrainCircuit className="w-4.5 h-4.5 text-white" />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-900">AI Workforce Engine</h3>
          <p className="text-xs text-slate-500">Smart assignment · demand forecasting · capacity planning</p>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {SUB.map((s) => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setSub(s.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${sub === s.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`ai-sub-${s.id}`}>
              <Icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          );
        })}
      </div>
      {sub === 'insights' && <InsightsTab goAssign={() => setSub('assign')} />}
      {sub === 'assign' && <AutoAssignTab />}
      {sub === 'balance' && <WorkforceBalanceTab />}
      {sub === 'market' && <MarketplaceTab />}
      {sub === 'sharing' && <SharingPoolsTab />}
      {sub === 'forecast' && <ForecastTab />}
      {sub === 'capacity' && <CapacityTab />}
      {sub === 'settings' && <AIPolicyTab />}
    </div>
  );
}

function InsightsTab({ goAssign }) {
  const [recs, setRecs] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await apiCall('/api/org/ai/recommendations');
      setRecs(r.recommendations || []);
    } catch (e) { toast.error(e.message); setRecs([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (recs === null) return <SkeletonRows n={3} />;
  return (
    <div className="space-y-3" data-testid="ai-insights-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{recs.length} actionable recommendation{recs.length === 1 ? '' : 's'}</p>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800" data-testid="ai-recs-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      {recs.map((r, i) => (
        <div key={i} className={`border rounded-xl p-4 ${SEVERITY[r.severity] || SEVERITY.info}`} data-testid={`ai-rec-${r.type}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold flex items-center gap-1.5">
                {r.severity === 'high' ? <ShieldAlert className="w-4 h-4 shrink-0" /> : r.severity === 'medium' ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <CheckCircle2 className="w-4 h-4 shrink-0" />}
                <span className="truncate">{r.title}</span>
              </p>
              <p className="text-xs mt-1 opacity-90">{r.detail}</p>
            </div>
            {(r.action === 'ai_assign' || r.action === 'auto_assign_all') && (
              <button onClick={goAssign}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-white/80 border border-current text-xs font-semibold hover:bg-white"
                data-testid={`ai-rec-action-${i}`}>
                {r.action === 'ai_assign' ? 'AI Assign' : 'Bulk Assign'}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function AutoAssignTab() {
  const [shifts, setShifts] = useState(null);
  const [mode, setMode] = useState('');
  const [priorities, setPriorities] = useState({});
  const [matchFor, setMatchFor] = useState(null); // {job, eligible, excluded}
  const [busy, setBusy] = useState('');
  const [bulkResults, setBulkResults] = useState(null);

  const load = useCallback(async () => {
    try {
      const [me, jobsRes, s, pr] = await Promise.all([
        apiCall('/api/auth/me'),
        apiCall('/api/jobs/search?status=active,draft&limit=200'),
        apiCall('/api/org/ai/settings'),
        apiCall('/api/org/ai/priorities?days=365'),
      ]);
      const myId = me.user?._id || me._id;
      setShifts((jobsRes.jobs || []).filter((j) => j.employer_id === myId && !j.assigned_worker_id));
      setMode(s.settings?.assignment_mode || 'manual');
      const pmap = {};
      (pr.shifts || []).forEach((p) => { pmap[p.job_id] = p; });
      setPriorities(pmap);
    } catch (e) { toast.error(e.message); setShifts([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runMatch = async (job) => {
    setBusy(`match-${job._id}`);
    try {
      const r = await apiCall(`/api/org/shifts/${job._id}/ai-match`, { method: 'POST' });
      setMatchFor({ job, eligible: r.eligible || [], excluded: r.excluded || [] });
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const runAiAssign = async (job) => {
    setBusy(`assign-${job._id}`);
    try {
      const r = await apiCall(`/api/org/shifts/${job._id}/ai-assign`, { method: 'POST' });
      if (r.action === 'assigned') toast.success(`Auto-assigned ${r.worker?.name || r.worker?.worker_name || 'best worker'} (score ${r.worker?.score})`);
      else if (r.action === 'suggested') toast.success(`AI suggests ${r.worker?.name || r.worker?.worker_name} (score ${r.worker?.score}) — confirm from candidates`);
      else if (r.action === 'no_candidate') toast.warning(r.reason || 'No eligible candidate found');
      else toast.info('Mode is manual — showing ranked candidates');
      if (r.action === 'assigned') load();
      else setMatchFor({ job, eligible: r.eligible || (r.worker ? [r.worker] : []), excluded: r.excluded || [] });
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const confirmAssign = async (job, workerId, name) => {
    setBusy(`confirm-${workerId}`);
    try {
      await apiCall(`/api/org/shifts/${job._id}/assign`, { method: 'POST', body: JSON.stringify({ worker_id: workerId, force: true }) });
      toast.success(`${name || 'Worker'} assigned to '${job.title}'`);
      setMatchFor(null); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const runBulk = async () => {
    setBusy('bulk');
    try {
      const r = await apiCall('/api/org/ai/auto-assign-all', { method: 'POST' });
      setBulkResults(r.results || []);
      const assigned = (r.results || []).filter((x) => x.action === 'assigned').length;
      const suggested = (r.results || []).filter((x) => x.action === 'suggested').length;
      toast.success(`Processed ${r.results?.length || 0} shifts — ${assigned} assigned, ${suggested} suggested`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  if (shifts === null) return <SkeletonRows n={4} />;
  return (
    <div className="space-y-3" data-testid="ai-assign-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-slate-500">
          {shifts.length} unfilled shift{shifts.length === 1 ? '' : 's'} · mode:{' '}
          <span className="font-semibold text-indigo-600 uppercase">{mode}</span>
        </p>
        <button onClick={runBulk} disabled={busy === 'bulk' || !shifts.length}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50"
          data-testid="ai-auto-assign-all-btn">
          {busy === 'bulk' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} AI Assign All
        </button>
      </div>
      {bulkResults && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1" data-testid="ai-bulk-results">
          {bulkResults.map((r, i) => (
            <p key={i} className="text-xs text-slate-600">
              <span className={`font-semibold ${r.action === 'assigned' ? 'text-emerald-600' : r.action === 'suggested' ? 'text-indigo-600' : 'text-amber-600'}`}>{r.action}</span>
              {' — '}{r.title}{r.worker ? ` → ${r.worker} (${r.score})` : ''}
            </p>
          ))}
        </div>
      )}
      {!shifts.length && <EmptyState label="No unfilled shifts — all upcoming shifts have workers assigned." />}
      <ul className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-xl">
        {shifts.slice(0, 50).map((j) => (
          <li key={j._id} className="px-4 py-3" data-testid={`ai-shift-row-${j._id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {priorities[j._id] && (
                    <span className={`mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${{ critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-amber-50 text-amber-600', low: 'bg-slate-100 text-slate-500' }[priorities[j._id].priority_level]}`}
                      title={(priorities[j._id].priority_factors || []).join(' · ')}
                      data-testid={`priority-chip-${j._id}`}>
                      {priorities[j._id].priority_level}
                    </span>
                  )}
                  {j.title}
                </p>
                <p className="text-xs text-slate-500">{String(j.start_time || '').slice(0, 16).replace('T', ' ')} · ${j.pay_rate}/hr</p>
                {j.ai_suggestion && (
                  <p className="text-xs text-violet-600 mt-0.5" data-testid={`ai-suggestion-${j._id}`}>
                    <Sparkles className="w-3 h-3 inline mr-0.5" /> AI suggests: {j.ai_suggestion.name} (score {j.ai_suggestion.score})
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => runMatch(j)} disabled={busy === `match-${j._id}`}
                  className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-medium hover:bg-slate-200 disabled:opacity-50"
                  data-testid={`ai-match-btn-${j._id}`}>
                  {busy === `match-${j._id}` ? 'Ranking…' : 'AI Match'}
                </button>
                <button onClick={() => runAiAssign(j)} disabled={busy === `assign-${j._id}`}
                  className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50"
                  data-testid={`ai-assign-btn-${j._id}`}>
                  {busy === `assign-${j._id}` ? 'Running…' : 'AI Assign'}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {matchFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setMatchFor(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="ai-match-modal">
            <h4 className="text-sm font-bold text-slate-900 mb-1">AI Candidates — {matchFor.job.title}</h4>
            <p className="text-xs text-slate-500 mb-3">Ranked by match score, reliability streak and availability.</p>
            {!matchFor.eligible.length && <p className="text-sm text-slate-400 py-4 text-center">No eligible candidates.</p>}
            <ul className="space-y-2">
              {matchFor.eligible.map((m) => (
                <li key={m.worker_id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-xl px-3 py-2.5" data-testid={`ai-candidate-${m.worker_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {m.name || m.worker_name || 'Worker'}
                      {m.badge && <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${BADGE_STYLES[m.badge] || ''}`}>{m.badge}</span>}
                    </p>
                    <p className="text-xs text-slate-500">streak {m.streak || 0} · {m.no_show_count || 0} no-shows</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-bold">{m.score}</span>
                    <button onClick={() => confirmAssign(matchFor.job, String(m.worker_id), m.name || m.worker_name)}
                      disabled={busy === `confirm-${m.worker_id}`}
                      className="px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`ai-candidate-assign-${m.worker_id}`}>
                      Assign
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            {matchFor.excluded.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-slate-500 mb-1">Excluded ({matchFor.excluded.length})</p>
                {matchFor.excluded.map((m) => (
                  <p key={m.worker_id} className="text-xs text-slate-400 truncate">
                    {m.name || m.worker_name}: {(m.blockers || []).join('; ')}
                  </p>
                ))}
              </div>
            )}
            <button onClick={() => setMatchFor(null)} className="mt-4 w-full py-2 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium hover:bg-slate-200" data-testid="ai-match-close">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ForecastTab() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => { (async () => {
    try {
      const r = await apiCall('/api/org/ai/forecast');
      setData(r.forecast);
    } catch (e) { toast.error(e.message); setData({}); }
  })(); }, []);

  const genNarrative = async () => {
    setGenBusy(true);
    try {
      const r = await apiCall('/api/org/ai/forecast?narrative=true');
      setData(r.forecast);
      if (r.narrative?.length) setInsights(r.narrative);
      else toast.warning(r.narrative_error ? `AI narrative unavailable: ${r.narrative_error}` : 'No AI insights returned');
    } catch (e) { toast.error(e.message); } finally { setGenBusy(false); }
  };

  if (data === null) return <SkeletonRows n={4} />;
  const maxAvg = Math.max(0.1, ...(data.weekday_demand || []).map((d) => d.avg_shifts));
  return (
    <div className="space-y-4" data-testid="ai-forecast-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Shifts (8 wks)" value={data.total_shifts_8w ?? 0} testid="forecast-total" />
        <Kpi label="Weekly avg (recent)" value={data.weekly_avg_recent ?? 0} testid="forecast-recent-avg" />
        <Kpi label="Trend" value={`${data.trend_pct > 0 ? '+' : ''}${data.trend_pct ?? 0}%`} tone={data.trend_pct > 0 ? 'up' : data.trend_pct < 0 ? 'down' : ''} testid="forecast-trend" />
        <Kpi label="Projected next week" value={data.projected_next_week ?? 0} testid="forecast-projection" />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-slate-500 mb-3">Demand by weekday (avg shifts)</p>
        <div className="flex items-end gap-2 h-28" data-testid="forecast-weekday-chart">
          {(data.weekday_demand || []).map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-[10px] text-slate-500">{d.avg_shifts}</span>
              <div className="w-full rounded-t-md bg-gradient-to-t from-indigo-600 to-violet-500"
                style={{ height: `${Math.max(4, (d.avg_shifts / maxAvg) * 80)}px` }} />
              <span className="text-[10px] font-medium text-slate-600">{d.day}</span>
            </div>
          ))}
        </div>
        {data.busiest_days?.length > 0 && (
          <p className="text-xs text-slate-500 mt-2">Busiest: <span className="font-semibold text-slate-700">{data.busiest_days.join(', ')}</span></p>
        )}
      </div>
      <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-violet-700 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> AI Planning Insights</p>
          <button onClick={genNarrative} disabled={genBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50"
            data-testid="forecast-generate-insights-btn">
            {genBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {genBusy ? 'Analyzing…' : 'Generate'}
          </button>
        </div>
        {insights ? (
          <ul className="space-y-1.5" data-testid="forecast-insights-list">
            {insights.map((s, i) => <li key={i} className="text-sm text-slate-700 flex gap-1.5"><CheckCircle2 className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />{s}</li>)}
          </ul>
        ) : <p className="text-xs text-slate-500">Click Generate for Claude-powered staffing insights based on your demand history.</p>}
      </div>
    </div>
  );
}

function CapacityTab() {
  const [cap, setCap] = useState(null);
  const [days, setDays] = useState(14);

  useEffect(() => { (async () => {
    try {
      const r = await apiCall(`/api/org/ai/capacity?days=${days}`);
      setCap(r.capacity);
    } catch (e) { toast.error(e.message); setCap({}); }
  })(); }, [days]);

  if (cap === null) return <SkeletonRows n={4} />;
  return (
    <div className="space-y-4" data-testid="ai-capacity-tab">
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
          <Kpi label="Active roster" value={cap.roster_active ?? 0} testid="capacity-roster" />
          <Kpi label="Available now" value={cap.available_now ?? 0} testid="capacity-available" />
          <Kpi label="Unfilled" value={cap.total_unfilled ?? 0} tone={cap.total_unfilled ? 'down' : ''} testid="capacity-unfilled" />
          <Kpi label="Fill rate" value={`${cap.fill_rate_pct ?? 100}%`} tone={cap.fill_rate_pct >= 90 ? 'up' : 'down'} testid="capacity-fill-rate" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        {[7, 14, 30].map((d) => (
          <button key={d} onClick={() => { setCap(null); setDays(d); }}
            className={`px-3 py-1 rounded-lg text-xs font-medium ${days === d ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}
            data-testid={`capacity-days-${d}`}>{d}d</button>
        ))}
        {cap.gap_days?.length > 0 && (
          <span className="text-xs text-amber-600 font-medium ml-2" data-testid="capacity-gap-days">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-0.5" />{cap.gap_days.length} day(s) with coverage gaps
          </span>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr><th className="text-left px-3 py-2 font-medium">Date</th><th className="text-right px-3 py-2 font-medium">Required</th><th className="text-right px-3 py-2 font-medium">Unfilled</th><th className="text-left px-3 py-2 font-medium w-1/3">Capacity</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(cap.days || []).map((d) => (
              <tr key={d.date} className={d.unfilled ? 'bg-amber-50/50' : ''} data-testid={`capacity-day-${d.date}`}>
                <td className="px-3 py-2 text-slate-700 font-medium">{d.date}</td>
                <td className="px-3 py-2 text-right text-slate-600">{d.required}</td>
                <td className={`px-3 py-2 text-right font-semibold ${d.unfilled ? 'text-amber-600' : 'text-slate-400'}`}>{d.unfilled}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full ${d.capacity_pct >= 100 ? 'bg-emerald-500' : d.capacity_pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(100, d.capacity_pct)}%` }} />
                    </div>
                    <span className="text-slate-500 w-9 text-right">{d.capacity_pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AIPolicyTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try {
      const r = await apiCall('/api/org/ai/settings');
      setSettings(r.settings);
    } catch (e) { toast.error(e.message); setSettings({}); }
  })(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiCall('/api/org/ai/settings', {
        method: 'PUT',
        body: JSON.stringify({
          assignment_mode: settings.assignment_mode,
          auto_assign_min_score: settings.auto_assign_min_score,
          fairness_enabled: !!settings.fairness_enabled,
          balance_enabled: !!settings.balance_enabled,
        }),
      });
      setSettings(r.settings);
      toast.success('AI policy saved');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (settings === null) return <SkeletonRows n={3} />;
  const MODES = [
    { id: 'manual', label: 'Recommend only', desc: 'AI ranks candidates — humans assign every shift manually.' },
    { id: 'suggest', label: 'Suggest first', desc: 'AI picks the best worker and stores a suggestion for one-click approval.' },
    { id: 'auto', label: 'Fully automatic', desc: 'AI instantly books the top eligible worker above your minimum score.' },
  ];
  return (
    <div className="space-y-4 max-w-xl" data-testid="ai-policy-tab">
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-500 uppercase">Assignment mode</p>
        {MODES.map((m) => (
          <label key={m.id} className={`flex items-start gap-3 border rounded-xl p-3.5 cursor-pointer transition ${settings.assignment_mode === m.id ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-200 hover:border-slate-300'}`}
            data-testid={`ai-mode-${m.id}`}>
            <input type="radio" name="ai-mode" checked={settings.assignment_mode === m.id}
              onChange={() => setSettings((prev) => ({ ...prev, assignment_mode: m.id }))} className="mt-0.5 accent-indigo-600" />
            <span>
              <span className="text-sm font-semibold text-slate-900 block">{m.label}</span>
              <span className="text-xs text-slate-500">{m.desc}</span>
            </span>
          </label>
        ))}
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Minimum match score for auto/suggest (0–100)</p>
        <input type="number" min={0} max={100} value={settings.auto_assign_min_score ?? 55}
          onChange={(e) => { const v = Number(e.target.value); setSettings((prev) => ({ ...prev, auto_assign_min_score: v })); }}
          className="w-32 border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900"
          data-testid="ai-min-score-input" />
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-500 uppercase">Balancing & fairness</p>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={settings.balance_enabled !== false}
            onChange={(e) => { const v = e.target.checked; setSettings((prev) => ({ ...prev, balance_enabled: v })); }}
            className="accent-indigo-600 w-4 h-4" data-testid="ai-balance-toggle" />
          Workload balancing — deduct match points from workers at high burnout risk
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={settings.fairness_enabled !== false}
            onChange={(e) => { const v = e.target.checked; setSettings((prev) => ({ ...prev, fairness_enabled: v })); }}
            className="accent-indigo-600 w-4 h-4" data-testid="ai-fairness-toggle" />
          Fairness engine — boost under-utilized workers so opportunities spread evenly
        </label>
      </div>
      <button onClick={save} disabled={saving}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
        data-testid="ai-policy-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save policy
      </button>
    </div>
  );
}

function Kpi({ label, value, tone, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={testid}>
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className={`text-lg font-bold ${tone === 'up' ? 'text-emerald-600' : tone === 'down' ? 'text-red-600' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}
